import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Package, Plus, Edit, Trash2, Store, Image, X, Loader2, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { shopApi, creatorPayoutApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

/* ── Shopify-style Variant Builder ── */
const VariantBuilder = ({ variants, setVariants, variantInventory, setVariantInventory }) => {
  const addVariantGroup = () => {
    setVariants([...variants, { name: '', values: [] }]);
  };

  const removeVariantGroup = (idx) => {
    const updated = variants.filter((_, i) => i !== idx);
    setVariants(updated);
    rebuildInventory(updated, variantInventory);
  };

  const updateGroupName = (idx, name) => {
    const updated = [...variants];
    updated[idx] = { ...updated[idx], name };
    setVariants(updated);
  };

  const addValue = (groupIdx, value) => {
    if (!value.trim()) return;
    const updated = [...variants];
    if (updated[groupIdx].values.includes(value.trim())) return;
    updated[groupIdx] = { ...updated[groupIdx], values: [...updated[groupIdx].values, value.trim()] };
    setVariants(updated);
    rebuildInventory(updated, variantInventory);
  };

  const removeValue = (groupIdx, valIdx) => {
    const updated = [...variants];
    updated[groupIdx] = { ...updated[groupIdx], values: updated[groupIdx].values.filter((_, i) => i !== valIdx) };
    setVariants(updated);
    rebuildInventory(updated, variantInventory);
  };

  const rebuildInventory = (vGroups, existingInv) => {
    const combos = getCombinations(vGroups);
    const newInv = {};
    combos.forEach(key => { newInv[key] = existingInv[key] ?? 0; });
    setVariantInventory(newInv);
  };

  return (
    <div className="space-y-3" data-testid="variant-builder">
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-400 font-medium">Product Variants</label>
        <button type="button" onClick={addVariantGroup}
          className="text-xs text-[#8B5CF6] hover:text-[#7C3AED] font-medium flex items-center gap-1"
          data-testid="add-variant-group-btn">
          <Plus className="w-3 h-3" /> Add option
        </button>
      </div>

      {variants.map((group, gIdx) => (
        <div key={gIdx} className="p-3 rounded-lg bg-white/[0.03] border border-white/5 space-y-2" data-testid={`variant-group-${gIdx}`}>
          <div className="flex items-center gap-2">
            <Input
              value={group.name}
              onChange={e => updateGroupName(gIdx, e.target.value)}
              placeholder="Option name (e.g. Size, Color)"
              className="bg-[#030014] border-[#8B5CF6]/20 h-9 text-sm flex-1"
              data-testid={`variant-group-name-${gIdx}`}
            />
            <button onClick={() => removeVariantGroup(gIdx)}
              className="p-1.5 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400"
              data-testid={`remove-variant-group-${gIdx}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {group.values.map((val, vIdx) => (
              <span key={vIdx} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#8B5CF6]/10 text-[#8B5CF6] text-xs font-medium">
                {val}
                <button onClick={() => removeValue(gIdx, vIdx)} className="hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <ValueInput onAdd={(v) => addValue(gIdx, v)} testId={`variant-value-input-${gIdx}`} />
          </div>
        </div>
      ))}
    </div>
  );
};

const ValueInput = ({ onAdd, testId }) => {
  const [val, setVal] = useState('');
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (val.trim()) { onAdd(val.trim()); setVal(''); }
    }
  };
  return (
    <input
      value={val}
      onChange={e => setVal(e.target.value)}
      onKeyDown={handleKey}
      onBlur={() => { if (val.trim()) { onAdd(val.trim()); setVal(''); } }}
      placeholder="Add value…"
      className="bg-transparent border-b border-dashed border-white/10 text-xs px-1 py-1 w-24 outline-none focus:border-[#8B5CF6] placeholder-gray-600"
      data-testid={testId}
    />
  );
};

/* ── Get all variant combinations ── */
function getCombinations(variantGroups) {
  const valid = variantGroups.filter(g => g.name && g.values.length > 0);
  if (valid.length === 0) return [];
  const combine = (groups) => {
    if (groups.length === 0) return [''];
    const [first, ...rest] = groups;
    const restCombos = combine(rest);
    const result = [];
    first.values.forEach(v => {
      restCombos.forEach(rc => {
        result.push(rc ? `${first.name}: ${v} / ${rc}` : `${first.name}: ${v}`);
      });
    });
    return result;
  };
  return combine(valid);
}

/* ── Per-variant inventory table ── */
const VariantInventoryTable = ({ variants, variantInventory, setVariantInventory, setTotalInventory }) => {
  const combos = getCombinations(variants);
  if (combos.length === 0) return null;

  const handleChange = (key, value) => {
    const val = Math.max(0, parseInt(value) || 0);
    const updated = { ...variantInventory, [key]: val };
    setVariantInventory(updated);
    setTotalInventory(Object.values(updated).reduce((s, v) => s + v, 0));
  };

  return (
    <div className="space-y-2" data-testid="variant-inventory-table">
      <label className="text-sm text-gray-400 font-medium">Stock per variant</label>
      <div className="rounded-lg border border-white/5 overflow-hidden">
        <div className="grid grid-cols-[1fr_100px] bg-white/[0.03] px-3 py-2 text-xs text-gray-400 font-medium">
          <span>Variant</span>
          <span className="text-right">Qty</span>
        </div>
        <div className="divide-y divide-white/5 max-h-48 overflow-y-auto">
          {combos.map(key => (
            <div key={key} className="grid grid-cols-[1fr_100px] items-center px-3 py-1.5">
              <span className="text-xs text-gray-300 truncate">{key}</span>
              <input
                type="number"
                min="0"
                value={variantInventory[key] ?? 0}
                onChange={e => handleChange(key, e.target.value)}
                className="w-full text-right bg-transparent border-b border-white/10 text-sm py-0.5 outline-none focus:border-[#8B5CF6]"
                data-testid={`inv-${key.replace(/[^a-zA-Z0-9]/g, '-')}`}
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[1fr_100px] px-3 py-2 bg-white/[0.03] text-xs font-medium border-t border-white/10">
          <span className="text-gray-400">Total</span>
          <span className="text-right text-[#00FF9D]">
            {Object.values(variantInventory).reduce((s, v) => s + (v || 0), 0)}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ── Main Products Panel ── */
const CreatorProductsPanel = ({ onRefresh }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [stripeConnected, setStripeConnected] = useState(null);
  const imgRef = useRef(null);

  const [form, setForm] = useState({
    name: '', description: '', price: '', category: 'merchandise',
    inventory_count: 0, images: [], variants: [], variant_inventory: {},
  });

  const fetchProducts = useCallback(async () => {
    try {
      const d = await shopApi.getMyProducts();
      setProducts(d.products || []);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Check Stripe connection status
  useEffect(() => {
    creatorPayoutApi.getStripeAccountStatus()
      .then(d => setStripeConnected(d.connected && d.charges_enabled))
      .catch(() => setStripeConnected(false));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', description: '', price: '', category: 'merchandise', inventory_count: 0, images: [], variants: [], variant_inventory: {} });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description, price: p.price.toString(),
      category: p.category, inventory_count: p.inventory_count,
      images: p.images || [], variants: p.variants || [],
      variant_inventory: p.variant_inventory || {},
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { toast.error('Name and price are required'); return; }
    const totalFromVariants = Object.values(form.variant_inventory).reduce((s, v) => s + (v || 0), 0);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      inventory_count: form.variants.length > 0 ? totalFromVariants : form.inventory_count,
    };
    try {
      if (editing) {
        await shopApi.updateProduct(editing.product_id, payload);
        toast.success('Product updated');
      } else {
        await shopApi.createProduct(payload);
        toast.success('Product created');
      }
      setShowForm(false);
      fetchProducts();
      onRefresh?.();
    } catch (e) { toast.error(e.message || 'Failed to save product'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await shopApi.deleteProduct(id); toast.success('Deleted'); fetchProducts(); onRefresh?.(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploadingImage(true);
    try {
      const result = await shopApi.uploadProductImage(file);
      setForm(prev => ({ ...prev, images: [result.url, ...(prev.images || []).slice(0, 4)] }));
      toast.success('Image uploaded');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingImage(false); e.target.value = ''; }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  // Stripe not connected: show warning
  if (stripeConnected === false) {
    return (
      <div className="space-y-6" data-testid="creator-products-panel">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Store className="w-5 h-5 text-[#00FF9D]" /> Products
          </h2>
        </div>
        <div className="admin-card p-8 text-center border border-yellow-500/20">
          <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Connect Stripe to Sell Products</h3>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            To create and sell products, you need to connect your Stripe account first.
            Go to the <span className="text-[#00FF9D] font-medium">Revenue</span> tab to set it up.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creator-products-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Store className="w-5 h-5 text-[#00FF9D]" /> Products
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">{products.length} product{products.length !== 1 ? 's' : ''} — 90% revenue to you</p>
        </div>
        <Button onClick={openNew} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> New Product
        </Button>
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="admin-card p-12 text-center">
          <Store className="w-14 h-14 mx-auto text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Products Yet</h3>
          <p className="text-gray-400 text-sm mb-6">Start selling merchandise to your fans</p>
          <Button onClick={openNew} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Your First Product
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(p => (
            <div key={p.product_id} className="admin-card overflow-hidden group" data-testid={`product-${p.product_id}`}>
              <div className="h-40 bg-gradient-to-br from-[#8B5CF6]/10 to-[#00FF9D]/5 flex items-center justify-center">
                {p.images?.[0] ? (
                  <img src={p.images[0].startsWith('/') ? `${window.location.origin}${p.images[0]}` : p.images[0]}
                    alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-12 h-12 text-gray-600" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-sm">{p.name}</h3>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${p.is_active ? 'bg-[#00FF9D]/15 text-[#00FF9D]' : 'bg-gray-500/15 text-gray-400'}`}>
                    {p.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-1 mb-3">{p.description}</p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-[#00FF9D]">${p.price.toFixed(2)}</span>
                  <span className="text-xs text-gray-500">{p.inventory_count} in stock</span>
                </div>
                {p.variants?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {p.variants.map(v => (
                      <span key={v.name} className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5CF6]/10 text-[#8B5CF6]">
                        {v.name}: {v.values.join(', ')}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <span>{p.total_sold || 0} sold</span>
                  <span>${(p.total_revenue || 0).toFixed(2)} rev.</span>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="outline" className="flex-1 border-[#8B5CF6]/30 h-8 text-xs"
                    onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`}>
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 h-8"
                    onClick={() => handleDelete(p.product_id)} data-testid={`delete-product-${p.product_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="admin-card p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="product-form-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold">{editing ? 'Edit Product' : 'New Product'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">Product Name *</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Studio T-Shirt" className="bg-[#030014] border-[#8B5CF6]/20"
                  data-testid="product-name-input" />
              </div>
              {/* Description */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">Description</label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe your product…" className="bg-[#030014] border-[#8B5CF6]/20 min-h-[80px]" />
              </div>
              {/* Price + Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-medium">Price (USD) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <Input type="number" min="0.50" max="500" step="0.01" value={form.price}
                      onChange={e => setForm({ ...form, price: e.target.value })}
                      className="bg-[#030014] border-[#8B5CF6]/20 pl-8" data-testid="product-price-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-medium">Category</label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-[#030014] border-[#8B5CF6]/20"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                      <SelectItem value="merchandise">Merchandise</SelectItem>
                      <SelectItem value="digital">Digital Download</SelectItem>
                      <SelectItem value="collectible">Collectible</SelectItem>
                      <SelectItem value="art">Art Print</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Variant Builder */}
              <VariantBuilder
                variants={form.variants}
                setVariants={v => setForm(prev => ({ ...prev, variants: v }))}
                variantInventory={form.variant_inventory}
                setVariantInventory={vi => setForm(prev => ({ ...prev, variant_inventory: vi }))}
              />

              {/* Per-variant inventory OR global inventory */}
              {form.variants.filter(g => g.name && g.values.length > 0).length > 0 ? (
                <VariantInventoryTable
                  variants={form.variants}
                  variantInventory={form.variant_inventory}
                  setVariantInventory={vi => setForm(prev => ({ ...prev, variant_inventory: vi }))}
                  setTotalInventory={t => setForm(prev => ({ ...prev, inventory_count: t }))}
                />
              ) : (
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-medium">Inventory</label>
                  <Input type="number" min="0" value={form.inventory_count}
                    onChange={e => setForm({ ...form, inventory_count: parseInt(e.target.value) || 0 })}
                    className="bg-[#030014] border-[#8B5CF6]/20" data-testid="product-inventory-input" />
                </div>
              )}

              {/* Images */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">Product Images</label>
                <input type="file" ref={imgRef} accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden" onChange={handleImageUpload} data-testid="product-image-input" />
                {form.images?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.images.map((img, idx) => (
                      <div key={idx} className="relative group/img">
                        <img src={img.startsWith('/') ? `${window.location.origin}${img}` : img}
                          alt="" className="w-16 h-16 object-cover rounded-lg border border-[#8B5CF6]/20" />
                        <button onClick={() => setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity">
                          <X className="w-2.5 h-2.5 text-white" />
                        </button>
                        {idx === 0 && <span className="absolute bottom-0.5 left-0.5 text-[9px] bg-[#8B5CF6] px-1 rounded">Main</span>}
                      </div>
                    ))}
                  </div>
                )}
                <Button type="button" variant="outline" className="w-full border-dashed border-[#8B5CF6]/30 hover:border-[#8B5CF6] h-9 text-sm"
                  onClick={() => imgRef.current?.click()} disabled={uploadingImage || (form.images?.length || 0) >= 5}
                  data-testid="upload-image-btn">
                  {uploadingImage ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <><Image className="w-4 h-4 mr-2" />{form.images?.length > 0 ? 'Add Another' : 'Upload Image'}</>}
                </Button>
                <p className="text-[10px] text-gray-600 mt-1">JPEG, PNG, WebP, GIF. Max 5MB, up to 5 images.</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowForm(false)} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={handleSave} className="flex-1 bg-[#00FF9D] hover:bg-[#00FF9D]/80 text-black font-medium" data-testid="save-product-btn">
                  {editing ? 'Update' : 'Create'} Product
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorProductsPanel;
